import { useState } from 'react';
import {
  FileTrigger,
  type FileTriggerProps as FileTriggerPropsAria,
} from 'react-aria-components';
import { FiPlus } from 'react-icons/fi';

import { Button, type ButtonProps } from '../Button/Button';
import { Description, Label } from '../Field/Field';

export type FileError = {
  name: string;
  reason: string;
};

export type FileUploadProps = Omit<
  FileTriggerPropsAria,
  'children' | 'className' | 'acceptedFileTypes'
> &
  ButtonProps & {
    label?: string;
    description?: string;
    buttonText?: string;
    allowsMultiple?: boolean;
    isRequired?: boolean;
    acceptedFileTypes?: readonly string[];
    maxFileSizeBytes?: number;
    onError?: (errors: FileError[]) => void;
  };

export function FileUpload({
  label,
  description,
  customStyles,
  buttonText = 'Upload File',
  acceptedFileTypes,
  allowsMultiple = false,
  isRequired,
  leftIcon,
  isDisabled,
  maxFileSizeBytes = 15 * 1024 * 1024, // 15MB default
  onError,
  ...props
}: FileUploadProps) {
  const [errors, setErrors] = useState<FileError[]>([]);

  const handleSelect = (files: FileList | null) => {
    if (files) {
      const newErrors = Array.from(files)
        .map((file) => {
          if (file.size > maxFileSizeBytes) {
            return {
              name: file.name,
              reason: `File too large (max ${Math.round(maxFileSizeBytes / (1024 * 1024))}MB).`,
            };
          }

          if (acceptedFileTypes && acceptedFileTypes.length > 0) {
            const extension = file.name.split('.').pop();
            if (
              !acceptedFileTypes.some(
                (supportedType) => extension === supportedType,
              )
            ) {
              return {
                name: file.name,
                reason: 'Invalid file type.',
              };
            }
          }

          return;
        })
        .filter((error): error is FileError => error !== undefined);

      setErrors(newErrors);

      if (newErrors.length > 0) {
        if (onError) {
          onError(newErrors);
        }
        return;
      }
    }
    setErrors([]);
    if (props.onSelect) {
      props.onSelect(files);
    }
  };

  return (
    <div className="relative">
      <FileTrigger
        {...props}
        data-testid="file-upload-input"
        acceptedFileTypes={
          acceptedFileTypes ? [...acceptedFileTypes] : undefined
        }
        allowsMultiple={allowsMultiple}
        onSelect={handleSelect}
      >
        {label && <Label isRequired={isRequired}>{label}</Label>}
        <Button
          {...props}
          leftIcon={leftIcon ?? <FiPlus />}
          label={buttonText}
          variant="secondary"
          width="auto"
          isDisabled={isDisabled}
          customStyles={customStyles}
          onPress={props.onPress}
        />
        {description && <Description>{description}</Description>}
      </FileTrigger>

      <div className="text-text-error text-sm flex items-start gap-2 absolute left-0 bottom-[-1.5rem] w-full">
        {errors.map((error) => (
          <span>{`${error.reason}`}</span>
        ))}
      </div>
    </div>
  );
}
